/**
 * External import
 */
import React, { useState, useEffect, useCallback, useMemo, memo, useRef, useImperativeHandle, forwardRef } from "react";
import { ButtonGroup, Button } from "react-bootstrap";
import { debounce } from 'lodash';

/**
 * Internal import
 */

import useFetchWithMsal from "../../../../hooks/useFetchWithMsal"; 
import { endpointsCf } from "../../../../config/componentsConfig";
import FileSelectorItem from "./FileSelectorItem";
import SummaryModal from "./SummaryModal";
import DeleteFileModal from "./DeleteFileModal";
import DocumentUploadButton from "./FileUpload/DocumentUploadButton";
import SummaryModalNew from "./SummaryModalNew";

  /**
   * Composant FileSelector
   * Permet la sélection de fichiers dans une arborescence avec gestion de dossiers extensibles
   * 
   * @component
   * @param {Object} props
   * @param {Function} props.setFilters - Fonction pour mettre à jour les filtres de sélection
   * @param {Object} props.filters - État actuel des filtres
   * @param {string} props.page - Identifiant de la page courante
   * @param {boolean} [props.isUpdatingCriterias=false] - Indique si les critères sont en cours de mise à jour
   * @param {string} props.selectedMode - Mode de sélection actuel
   */
  export interface FileSelectorProps {
    setFilters: (filters: any) => void;
    setSelectedMenu: (selectedMenu: number) => void;
    filters: any;
    page: string;
    isUpdatingCriterias?: boolean;
    selectedMode: string;
    IsUpdatingFiles?: boolean;
    setIsUpdatingFiles?: (value: boolean) => void;
    setSummaryView: (summaryView: {viewed: boolean, doc: string, summary: string}) => void;
  }

  
  const FileSelector = forwardRef<any, FileSelectorProps>(({ 
    setFilters, 
    setSelectedMenu,
    filters, 
    page, 
    isUpdatingCriterias, 
    selectedMode, 
    IsUpdatingFiles = false,
    setIsUpdatingFiles = () => {},
    setSummaryView,
  }, ref) => {

     /**État contenant l'arborescence complète des fichiers et dossiers
     *@type {Array<FileTree>} //Structure: [{ id: string, name: string, type: 'file'|'folder', children?: FileTree[] }]
     */
     const [files, setFiles] = useState([]);

    /**
     * État contenant les IDs des fichiers sélectionnés
     * Utilisé pour gérer la sélection multiple de fichiers
     * @type {Array<string>}
     */
    const [selectedFiles, setSelectedFiles] = useState([]);

    /**
     * Set contenant les IDs des dossiers actuellement déployés
     * Permet de gérer l'état d'expansion/réduction des dossiers
     * @type {Set<string>}
     */
    const [expandedFolders, setExpandedFolders] = useState(new Set());

    /**
     * Indicateur de rafraîchissement en cours de l'arborescence
     * Évite les requêtes multiples simultanées
     * @type {boolean}
     */
    const [isRefreshing, setIsRefreshing] = useState(false);

    /**
     * État pour stocker l'existence des résumés
     * @type {Map<string, boolean>}
     */
    const [summaryExiste, setSummaryExiste] = useState<Map<string, boolean>>(new Map());

    const [docSummary, setDocSummary] = useState({title: "", long: "", short: ""});
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState({show: false, item: null});
    const [displaySummaryModal, setDisplaySummaryModal] = useState({show: false, fileName: "", taskType: "summarize", path: ""});
    const containerRef = useRef(null);
    /**
     * Hook personnalisé pour les requêtes authentifiées avec MSAL
     * @type {Object}
     * @property {Function} execute - Fonction pour exécuter une requête
     * @property {boolean} isLoading - Indique si une requête est en cours
     */
    const { execute, isLoading} = useFetchWithMsal();

    /**
     * Cache mémorisé pour stocker les informations des fichiers par ID
     * Optimise la recherche des informations de fichiers
     * @type {Map<string, FileTree>}
     */
    const fileIdsCache = useMemo(() => new Map(), []);

    // Ajouter la référence pour suivre les changements de filtres
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    // ---------------------Utils Mise à jour (arborescence et selection)---------------------

    const selectFilebyName = (name: string) => {
      const file = files.find(file => file.name === name);
      if (file) {
        setSelectedFiles([String(file.id)]);
      }
    }
    
    const getAllFileIdsInFolder = (items: any[]) => {
      /**
     * Récupère tous les IDs des fichiers dans un dossier
     * @param items - Les éléments à parcourir
     * @returns Un tableau contenant tous les IDs des fichiers dans le dossier
     */
      let fileIds: string[] = [];
      items.forEach(item => {
        if (item.type === 'folder' && item.children) {
          fileIds = [...fileIds, ...getAllFileIdsInFolder(item.children)];
        } else if (item.type !== 'folder') {
          fileIds.push(String(item.id));
        }
      });
      return fileIds;
    };
  
    
    const findFileById = (items: any[], id: string): any => {
      /**
     * Recherche un fichier par son ID
     * @param items - Les éléments à parcourir
     * @param id - L'ID du fichier à trouver
     * @returns Le fichier trouvé ou null si non trouvé
     */
      for (const item of items) {
        if (String(item.id) === id) return item;
        if (item.children) {
          const found = findFileById(item.children, id);  
          if (found) return found;
        }
      }
      return null;
    };

    const updateFilters = useCallback(
      debounce((selectedFilesArg: string[], filesArg: any[]) => {
        const selectedPaths = selectedFilesArg.map(id => {
          const file = fileIdsCache.get(id) || findFileById(filesArg, id);
          if (file) {
            fileIdsCache.set(id, file);
            return file.path;
          }
          return null;
        }).filter(Boolean);

        setFilters(current => ({
          ...current,
          path: selectedPaths.length > 0 ? selectedPaths : []
        }));
      }, 200),
      [fileIdsCache, setFilters]
    );
  
    
    const handleFileSelect = useCallback((item) => {
      /**
     * Gestion de la sélection des fichiers
     * @param item - L'élément à sélectionner
     */
      const itemId = String(item.id);
      if (item.type === 'folder') {
        const fileIds = getAllFileIdsInFolder(item.children);
        setSelectedFiles(prevSelected => {
          const prevSelectedSet = new Set(prevSelected);
          const allSelected = fileIds.every(id => prevSelectedSet.has(id));
          
          if (allSelected) {
            return prevSelected.filter(id => !fileIds.includes(id));
          } else {
            return [...new Set([...prevSelected, ...fileIds])];
          }
        });
      } else {
        setSelectedFiles(prevSelected => {
          if (prevSelected.includes(itemId)) {
            return prevSelected.filter(id => id !== itemId);
          }
          return [...prevSelected, itemId];
        });
      }
    }, []);

    
    const selectAllFiles = useCallback(() => {
     /**
     * Sélectionner tous les fichiers
     */
      const allFileIds = getAllFileIdsInFolder(files);
      const uniqueFileIds = [...new Set(allFileIds)];
      setSelectedFiles(uniqueFileIds);
      updateFilters.cancel();
      const selectedPaths = uniqueFileIds.map(id => {
        const file = fileIdsCache.get(id) || findFileById(files, id);
        if (file) {
          fileIdsCache.set(id, file);
          return file.path;
        }
        return null;
      }).filter(Boolean);
      setFilters(current => ({
        ...current,
        path: selectedPaths.length > 0 ? selectedPaths : []
      }));
    }, [files, updateFilters, fileIdsCache, setFilters]);
    
    const getFolderSelectionState = useCallback((items) => {
      /**
     * Détermine l'état de sélection d'un dossier
     * 
     * @param {FileTree[]} items - Les éléments du dossier à vérifier
     * @returns {'none' | 'partial' | 'full'} État de sélection :
     *   - 'none' : aucun fichier sélectionné
     *   - 'partial' : certains fichiers sélectionnés
     *   - 'full' : tous les fichiers sélectionnés
     */
      if (!items || items.length === 0) return 'none';

      const fileIds = getAllFileIdsInFolder(items);
      if (fileIds.length === 0) return 'none';

      const selectedCount = fileIds.filter(id => selectedFiles.includes(id)).length;
      
      if (selectedCount === 0) return 'none';
      if (selectedCount === fileIds.length) return 'full';
      return 'partial';
    }, [selectedFiles, getAllFileIdsInFolder]);

    
    const flattenedItems = useMemo(() => {
      /**
     * Convertit l'arborescence hiérarchique en liste plate pour l'affichage
     * Prend en compte l'état d'expansion des dossiers pour n'inclure que
     * les éléments visibles
     * 
     * @type {Array<FlattenedItem>}
     * Structure: Array<{
     *   ...FileTree,
     *   level: number // Niveau de profondeur dans l'arborescence
     * }>
     */
      const flatList = [];
      
      /**
       * Fonction récursive pour aplatir l'arborescence
       * @param {FileTree[]} items - Éléments à aplatir
       * @param {number} level - Niveau de profondeur actuel
       */
      const flatten = (items, level = 0) => {
        items.forEach(item => {
          flatList.push({ ...item, level });
          if (item.type === 'folder' && expandedFolders.has(item.id) && item.children) {
            flatten(item.children, level + 1);
          }
        });
      };
      
      flatten(files);
      return flatList;
    }, [files, expandedFolders]);

    // ---------------------Fonctions de Mise à jours-------------------------------

    const refreshFiles = useCallback(async (SelectAll = false) => {
      /**
     * Rafraîchit l'arborescence des fichiers depuis le serveur
     * Met à jour l'état files et sélectionne tous les fichiers par défaut
     * 
     * @throws {Error} En cas d'échec de la requête
     * @returns {Promise<void>}
     */
      if (isRefreshing) {
        console.log("No refreshFiles (isRefreshing)");
        return;
      }
      try {
        setIsRefreshing(true);
        
        console.log("refreshFiles", filters);
        const data = await execute("POST", endpointsCf.docTree(page), {
          filters,
          selectedMode
        });
        
        //Check Summaries
        if(selectedMode === "MYGEORGES") {

          const filesWithoutSummary = data
            .filter(file => {
              // Modifier la condition pour continuer à vérifier les fichiers qui ont hasSummary = false
              return file.type !== 'folder' && (
                !summaryExiste.has(file.id) || 
                summaryExiste.get(file.id) === false
              );
            })
            .map(file => ({
              fileId: file.id,
              fileName: file.name,
              mode: selectedMode === "MYGEORGES" ? "mygeorges" : page,
              user_id: filters?.user_id?.[0]
            }));

          if (filesWithoutSummary.length === 0) return;

          try {
            // Faire une requête par document
            const results = await Promise.all(
              filesWithoutSummary.map(fileInfo => 
                execute("POST", endpointsCf.hasSummary, fileInfo)
              )
            );

            // Créer une nouvelle Map avec toutes les valeurs existantes
            const newSummaryExiste = new Map(summaryExiste);
            
            // Ajouter les nouvelles valeurs
            results.forEach((result, index) => {
              const fileId = filesWithoutSummary[index].fileId;
              console.log(`File ${filesWithoutSummary[index].fileName}: hasSummary = ${result.hasSummary}`);
              newSummaryExiste.set(fileId, result.hasSummary);
            });
            
            console.log('New summaryExiste Map:', Object.fromEntries(newSummaryExiste));
            setSummaryExiste(newSummaryExiste);
          } catch (err) {
            console.error('Error checking summaries:', err);
          }
        }
        
        if (data && Array.isArray(data)) {
          setFiles(data);
          const allFileIds = getAllFileIdsInFolder(data);
          if (SelectAll) {
            setSelectedFiles(allFileIds);
            // Même comportement que selectAllFiles : le payload chat doit avoir `path` tout de suite (pas seulement après le debounce).
            updateFilters.cancel();
            const selectedPaths = allFileIds
              .map((id) => {
                const file = fileIdsCache.get(id) || findFileById(data, id);
                if (file) {
                  fileIdsCache.set(id, file);
                  return file.path;
                }
                return null;
              })
              .filter(Boolean);
            setFilters((current) => ({
              ...current,
              path: selectedPaths.length > 0 ? selectedPaths : [],
            }));
          }
        }
      } catch (err) {
        console.error('FetchFiles error:', err);
      } finally {
        setIsRefreshing(false);
      }
    }, [
      execute,
      filters,
      page,
      isRefreshing,
      selectedMode,
      updateFilters,
      setFilters,
      fileIdsCache,
      summaryExiste,
    ]);

    useImperativeHandle(ref, () => ({
      refreshFiles: () => {
        refreshFiles();
      },
      selectFilebyName: (name: string) => {
        selectFilebyName(name);
      }
    }));

    // ---------------------Mise à jours -------------------------------

    useEffect(() => {
    /**
    * Récupération de l'arborescence des fichiers au montage du composant
    */
      refreshFiles(false);
    }, [execute, page]);

    useEffect(() => {
      /**
       * Récupération de l'arborescence des fichiers lors d'un changement de PageFilters
       */
      console.log("isUpdatingCriterias", isUpdatingCriterias);
      if (!isRefreshing && isUpdatingCriterias) {
        refreshFiles(false);
      } else {
        console.log("No refreshFiles (PagesFilters not changed)");
      }
    }, [isUpdatingCriterias]);

    useEffect(() => {
      /**
       * Récupération de l'arborescence des fichiers 
       */
      console.log("IsUpdatingFiles", IsUpdatingFiles);
      if (!isRefreshing && IsUpdatingFiles) {
        refreshFiles(false);
      } else {
        console.log("No refreshFiles (PagesFilters not changed)");
      }
      
      // Vérifier que setIsUpdatingFiles est une fonction avant de l'appeler
      if (typeof setIsUpdatingFiles === 'function') {
        setIsUpdatingFiles(false);
      }
    }, [IsUpdatingFiles, refreshFiles, isRefreshing]);

    // Ajouter un useEffect pour rafraîchir quand le mode change
    useEffect(() => {
      console.log("Mode changed, refreshing files...");
      refreshFiles(false);
    }, [selectedMode]);

    
    useEffect(() => {
      /**
       * Mise à jour des filtres avec un délai pour éviter les mises à jour trop fréquentes
       */
      updateFilters(selectedFiles, files);
      return () => {
        updateFilters.cancel();
      };
    }, [selectedFiles, files, setFilters, updateFilters]);

    // Ajouter un useEffect pour écouter l'événement newThread
    useEffect(() => {
      const handleNewThread = () => {
        console.log("New thread created, refreshing files...");
        refreshFiles(false);
      };

      window.addEventListener('newThread', handleNewThread);

      // Cleanup
      return () => {
        window.removeEventListener('newThread', handleNewThread);
      };
    }, [refreshFiles]);

    // ---------------------CONTROLS ---------------------

    const toggleFolder = (folderId) => {
      /**
       * Bascule l'état d'expansion d'un dossier
       */
      setExpandedFolders(prev => {
        const newSet = new Set(prev);
        if (newSet.has(folderId)) {
          newSet.delete(folderId);
        } else {
          newSet.add(folderId);
        }
        return newSet;
      });
    };
    
    const deselectAllFiles = useCallback(() => {
     /**
     * Désélectionne tous les fichiers
     */
      setSelectedFiles([]);
      updateFilters.cancel();
      setFilters(current => ({
        ...current,
        path: []
      }));
    }, [updateFilters, setFilters]);

    const toggleAllFilesSelection = useCallback(() => {
      if (selectedFiles.length > 0) {
        deselectAllFiles();
      } else {
        selectAllFiles();
      }
    }, [selectedFiles.length, deselectAllFiles, selectAllFiles]);
    
    const selectTheseFiles = useCallback((item) => {
    /**
     * Désélectionne tous les fichiers puis sélectionne l'élément spécifié
     * @param {FileTreeItem} item - L'élément (fichier ou dossier) à sélectionner exclusivement
     */
      deselectAllFiles();
      handleFileSelect(item);
    }, []);
    

    // ---------------------COMPOSANTS ---------------------

    const FlatFileTreeList = useMemo(
      () => (
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
          {flattenedItems.map((item, index) => (
            <FileSelectorItem
              key={`${item.id}-${selectedFiles.includes(item.id) ? "1" : "0"}-${summaryExiste.get(item.id) ? "s1" : "s0"}`}
              index={index}
              getFolderSelectionState={getFolderSelectionState}
              handleFileSelect={handleFileSelect}
              selectTheseFiles={selectTheseFiles}
              toggleFolder={toggleFolder}
              selectedFiles={selectedFiles}
              selectedMode={selectedMode}
              flattenedItems={flattenedItems}
              summaryExiste={summaryExiste}
              setDocSummary={setDocSummary}
              setShowSummaryModal={setShowSummaryModal}
              page={page}
              filters={filters}
              setSelectedFiles={setSelectedFiles}
              setFiles={setFiles}
              setSummaryExiste={setSummaryExiste}
              expandedFolders={expandedFolders}
              style={{ height: 35 }}
              setShowDeleteModal={setShowDeleteModal}
              setDisplaySummaryModal={setDisplaySummaryModal}
            />
          ))}
        </div>
      ),
      [
        flattenedItems,
        selectedFiles,
        selectedMode,
        summaryExiste,
        filters,
        page,
        getFolderSelectionState,
        handleFileSelect,
        selectTheseFiles,
        toggleFolder,
        expandedFolders,
      ]
    );

    // Fonction pour vérifier l'existence des résumés
    const checkSummaries = useCallback(async () => {
      if (selectedMode !== "MYGEORGES") return;

      const filesWithoutSummary = files
        .filter(file => {
          // Modifier la condition pour continuer à vérifier les fichiers qui ont hasSummary = false
          return file.type !== 'folder' && (
            !summaryExiste.has(file.id) || 
            summaryExiste.get(file.id) === false
          );
        })
        .map(file => ({
          fileId: file.id,
          fileName: file.name,
          page: page,
          selectedMode: selectedMode,
          user_id: filters?.user_id?.[0]
        }));

      if (filesWithoutSummary.length === 0) return;

      try {
        // Faire une requête par document
        const results = await Promise.all(
          filesWithoutSummary.map(fileInfo => 
            execute("POST", endpointsCf.hasSummary, fileInfo)
          )
        );

        // Créer une nouvelle Map avec toutes les valeurs existantes
        const newSummaryExiste = new Map(summaryExiste);
        
        // Ajouter les nouvelles valeurs
        results.forEach((result, index) => {
          const fileId = filesWithoutSummary[index].fileId;
          console.log(`File ${filesWithoutSummary[index].fileName}: hasSummary = ${result.hasSummary}`);
          newSummaryExiste.set(fileId, result.hasSummary);
        });
        
        console.log('New summaryExiste Map:', Object.fromEntries(newSummaryExiste));
        setSummaryExiste(newSummaryExiste);
      } catch (err) {
        console.error('Error checking summaries:', err);
      }
    }, [execute, files, page, selectedMode, summaryExiste, filters]);

    // Effet pour vérifier périodiquement les résumés
    /*useEffect(() => {
      const interval = setInterval(checkSummaries, 2000);
      return () => clearInterval(interval);
    }, [checkSummaries])*/

    /*useEffect(() => {
      if(!isLoading) {
        checkSummaries();
      }
    }, [flattenedItems?.length, checkSummaries, isLoading])*/

    // Effet pour réinitialiser summaryExiste quand les fichiers changent
    useEffect(() => {
      setSummaryExiste(new Map());
    }, [selectedMode]);

    //depreciated, no one has access to all deals
    const needToSelectDeal = page === "mydeal" && selectedMode !== "MYGEORGES" && !(filters?.subIndustry?.length > 0);

    return (
      <div className="d-flex flex-column" style={{ flex: 1, width: "100%", minHeight: 0, height: "100%" }}>
        {selectedMode === "MYGEORGES" && <div style={{paddingLeft: "10%", paddingRight: "10%", height: "15%"}}>
          <DocumentUploadButton filesNumber={files.length} setSelectedMenu={setSelectedMenu}/>
        </div>}
        <div className="d-flex justify-content-between align-items-start px-3 pt-3" style={{ flexShrink: 0 }}>
          <h6 className="mb-0">Available Documents</h6>
          <div className="d-flex align-items-center gap-2">
            <small
              className="text-muted"
              style={{
                whiteSpace: "nowrap",
                padding: "2px 8px",
                border: "1px solid #e6e6e6",
                borderRadius: 12,
              }}
            >
              {(selectedFiles.length > 9999 ? ">" : "") + selectedFiles.length + " selected"}
            </small>
            <ButtonGroup size="sm">
              <Button 
                variant={isLoading ? "primary" : "primary-outlined"}
                onClick={refreshFiles}
                disabled={isLoading}
                title="Refresh"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </Button>
              <Button 
                variant="secondary-outlined"
                onClick={selectAllFiles}
                disabled={isLoading || files.length === 0}
                title="Select All"
              >
                <i className="bi bi-check-all"></i>
              </Button>
              <Button 
                variant="secondary-outlined"
              onClick={toggleAllFilesSelection}
              disabled={isLoading || files.length === 0}
              title={selectedFiles.length > 0 ? "Unselect All" : "Select All"}
              >
              <i
                className={`bi ${
                  selectedFiles.length > 0 ? "bi-square" : "bi-check-square"
                }`}
              ></i>
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div ref={containerRef} style={{ minWidth: '100%', minHeight: 0, overflow: 'hidden', flexGrow: 1, display: "flex", flexDirection: "column" }}>
          {selectedMode === "MYGEORGES" && 
            <div style={{fontSize: "0.8rem", color: "grey", marginBottom: 10}}> If you don't see all your files, do not hesitate to refresh here:
              <Button 
              variant={isLoading ? "primary" : "primary-outlined"}
              onClick={refreshFiles}
              disabled={isLoading}
              title="Refresh"
            >
              <i className="bi bi-arrow-clockwise"></i>
            </Button>
            </div>}
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : files.length === 0 ? (
            <p className="text-muted">No documents found</p>
          ) : FlatFileTreeList}
        </div> 
        <div
          className="px-3 pb-3 border-top pt-2"
          style={{
            width: "100%",
            position: "sticky",
            bottom: 0,
            flexShrink: 0,
            background: "#fff",
            zIndex: 2,
            minHeight: 44,
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {(selectedFiles.length > 9999 ? ">" : "") + selectedFiles.length + " document" + (selectedFiles.length !== 1 ? 's' : '') + " selected"}
            </small>
            <small className="text-muted" style={{display: "flex", flex: 1, justifyContent: "flex-end", width: "100%"}}> 
              {selectedMode === "MYGEORGES" && files.length + " / 15 documents"}
            </small>
          </div>
        </div>
          {/*<div style={{position: "fixed", bottom: 0, width: "100%"}}>
            {selectedMode === "MYGEORGES" && <FileUploader filesNumber={files.length} page={page} refreshFiles={refreshFiles} checkSummaries={checkSummaries}/>}
          </div>*/}
        <SummaryModal 
          showSummaryModal={showSummaryModal} 
          setShowSummaryModal={setShowSummaryModal} 
          docSummary={docSummary} 
          setDocSummary={setDocSummary}
          setSummaryView={setSummaryView}
        />
        <DeleteFileModal 
          show={showDeleteModal} 
          setShow={setShowDeleteModal} 
          page={page}
          selectedMode={selectedMode}
          setSelectedFiles={setSelectedFiles}
          setFiles={setFiles}
          setSummaryExiste={setSummaryExiste}
        />
        <SummaryModalNew
          showSummaryModal={displaySummaryModal.show}
          setShowSummaryModal={(value: boolean) => setDisplaySummaryModal({show: value, fileName: "", taskType: "summarize", path: ""})}
          fileName={displaySummaryModal.fileName}
          taskType={displaySummaryModal.taskType}
          page={page}
          path={displaySummaryModal.path}
          selectedMode={selectedMode}
          setSummaryView={setSummaryView}
          setSelectedMenu={setSelectedMenu}
      />
      </div>
    );
  });
  
  export default FileSelector;
  
            